import type { operations } from "@tamariba/api-client";

export type Drill =
  operations["saveDrill"]["responses"][200]["content"]["application/json"];
export type DrillDetailData =
  operations["getDrill"]["responses"][200]["content"]["application/json"];
export type DrillWrite =
  operations["saveDrill"]["requestBody"]["content"]["application/json"];
export type DrillCollection =
  operations["listDrillCollections"]["responses"][200]["content"]["application/json"][number];
export type DrillCollectionDetail =
  operations["getDrillCollection"]["responses"][200]["content"]["application/json"];
export type DrillCollectionWrite =
  operations["saveDrillCollection"]["requestBody"]["content"]["application/json"];
export type DrillCollectionOrder =
  operations["getDrillCollectionOrder"]["responses"][200]["content"]["application/json"];
export type DrillCollectionOrderWrite =
  operations["saveDrillCollectionOrder"]["requestBody"]["content"]["application/json"];
export type DrillCollectionOrderSaved =
  operations["saveDrillCollectionOrder"]["responses"][200]["content"]["application/json"];
export type Practice =
  operations["saveDrillPractice"]["responses"][200]["content"]["application/json"];
export type PracticeWrite =
  operations["saveDrillPractice"]["requestBody"]["content"]["application/json"];
export type PracticeSession =
  operations["startDrillPractice"]["responses"][200]["content"]["application/json"];
export type Layout =
  operations["listLayouts"]["responses"][200]["content"]["application/json"][number];

export const stages = [
  { streak: 1, sets: 5, label: "1回成功", hint: "まずは、同じ配置を確実に。" },
  { streak: 2, sets: 3, label: "2回連続成功", hint: "もう一度、同じ感覚で。" },
  { streak: 3, sets: 2, label: "3回連続成功", hint: "再現できるショットに。" },
  { streak: 5, sets: 1, label: "5回連続成功", hint: "集中をつないで、5球。" },
] as const;

export function progress(outcomes: boolean[], stage: number) {
  const target = stages[stage] ?? stages[0];
  let streak = 0;
  let sets = 0;
  for (const success of outcomes) {
    streak = success ? streak + 1 : 0;
    if (streak === target.streak) {
      sets += 1;
      streak = 0;
    }
  }
  return {
    streak,
    sets,
    complete: sets === target.sets,
    successes: outcomes.filter(Boolean).length,
  };
}

export function today() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function scheduled(date: string | null) {
  return date !== null && date <= today();
}

export function dateLabel(date: string) {
  return new Date(
    date.length === 10 ? `${date}T00:00:00` : date,
  ).toLocaleDateString("ja-JP", {
    month: "numeric",
    day: "numeric",
  });
}

export class RequestError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function request<T>(
  path: string,
  body?: unknown,
  method: "POST" | "PUT" = "POST",
): Promise<T> {
  const response = await fetch(
    path,
    body === undefined
      ? { cache: "no-store" }
      : {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
  ).catch(() => {
    throw new RequestError(
      0,
      "接続できませんでした。通信環境を確認して、もう一度お試しください。",
    );
  });
  if (!response.ok) {
    let message =
      "接続できませんでした。通信環境を確認して、もう一度お試しください。";
    if (response.status === 401)
      message =
        "ログインの有効期限が切れました。別のタブでログインし直してから、再試行してください。";
    else if (response.status === 403)
      message =
        "この操作へのアクセス権がありません。管理者権限やドリルの公開設定を確認してください。";
    else if (response.status === 409)
      message =
        "保存内容が変わっています。入力内容を控えてから、最新の画面を開いてください。";
    else if (response.status === 400 || response.status === 404) {
      const data = await response.json().catch(() => null);
      if (typeof data?.message === "string") message = data.message;
    }
    throw new RequestError(response.status, message);
  }
  return response.json() as Promise<T>;
}
